import * as THREE from "three";
import type { PlanetVisuals, PlanetClass, PlanetPalette, PlanetAtmosphere } from "../types/game";

// ── Seeded PRNG (mulberry32) ──

function seedFromString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Simplex 2D noise (self-contained, no dependency) ──

const GRAD2 = [
  [1, 1], [-1, 1], [1, -1], [-1, -1],
  [1, 0], [-1, 0], [0, 1], [0, -1],
];

function buildPermTable(rng: () => number): Uint8Array {
  const p = new Uint8Array(512);
  const base = new Uint8Array(256);
  for (let i = 0; i < 256; i++) base[i] = i;
  // Fisher-Yates shuffle with seeded rng
  for (let i = 255; i > 0; i--) {
    const j = (rng() * (i + 1)) | 0;
    const tmp = base[i]; base[i] = base[j]; base[j] = tmp;
  }
  for (let i = 0; i < 512; i++) p[i] = base[i & 255];
  return p;
}

function createNoise2D(rng: () => number) {
  const perm = buildPermTable(rng);
  const F2 = 0.5 * (Math.sqrt(3) - 1);
  const G2 = (3 - Math.sqrt(3)) / 6;

  return (x: number, y: number): number => {
    const s = (x + y) * F2;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const t = (i + j) * G2;
    const X0 = i - t, Y0 = j - t;
    const x0 = x - X0, y0 = y - Y0;
    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;
    const x1 = x0 - i1 + G2, y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2, y2 = y0 - 1 + 2 * G2;
    const ii = i & 255, jj = j & 255;

    let n0 = 0, n1 = 0, n2 = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) {
      t0 *= t0;
      const g = GRAD2[perm[ii + perm[jj]] & 7];
      n0 = t0 * t0 * (g[0] * x0 + g[1] * y0);
    }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) {
      t1 *= t1;
      const g = GRAD2[perm[ii + i1 + perm[jj + j1]] & 7];
      n1 = t1 * t1 * (g[0] * x1 + g[1] * y1);
    }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) {
      t2 *= t2;
      const g = GRAD2[perm[ii + 1 + perm[jj + 1]] & 7];
      n2 = t2 * t2 * (g[0] * x2 + g[1] * y2);
    }
    return 70 * (n0 + n1 + n2); // Range roughly -1 to 1
  };
}

// Triplanar noise — sample 2D noise from 3 orthogonal planes and blend to
// avoid stretching when mapped to a sphere surface
function triplanarFbm(
  noise: (x: number, y: number) => number,
  nx: number, ny: number, nz: number,
  scale: number, octaves: number, lacunarity = 2.0, gain = 0.5,
): number {
  // Blend weights based on the dominant axis of the sphere normal
  const wx = nx * nx;
  const wy = ny * ny;
  const wz = nz * nz;
  const wSum = wx + wy + wz;
  return (
    wx * fbm(noise, ny * scale, nz * scale, octaves, lacunarity, gain) +
    wy * fbm(noise, nx * scale, nz * scale, octaves, lacunarity, gain) +
    wz * fbm(noise, nx * scale, ny * scale, octaves, lacunarity, gain)
  ) / wSum;
}

// Fractional Brownian Motion — layered noise for terrain detail
function fbm(noise: (x: number, y: number) => number, x: number, y: number, octaves: number, lacunarity = 2.0, gain = 0.5): number {
  let val = 0, amp = 1, freq = 1, max = 0;
  for (let i = 0; i < octaves; i++) {
    val += noise(x * freq, y * freq) * amp;
    max += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return val / max;
}

// ── Planet class presets ──

interface FullPlanetConfig {
  radius: number;
  palette: PlanetPalette;
  surfaceDetail: string;
  liquidCoverage: number;
  atmosphere: PlanetAtmosphere;
  rotationSpeed: number;
  rings?: PlanetVisuals["rings"];
}

const PRESETS: Record<PlanetClass, Omit<FullPlanetConfig, "radius" | "rings">> = {
  terran: {
    palette: { primary: "#4a8a4a", secondary: "#8a7a4a", liquid: "#1a5a8a", polar: "#d0e8f0" },
    surfaceDetail: "continents",
    liquidCoverage: 0.65,
    atmosphere: { color: "#4ecdc4", density: 0.6, hasCloud: true, cloudColor: "#f0f8ff", cloudCoverage: 0.4 },
    rotationSpeed: 0.003,
  },
  arid: {
    palette: { primary: "#b8845a", secondary: "#946840", liquid: "#7a6530", polar: "#c8b898" },
    surfaceDetail: "continents",
    liquidCoverage: 0.1,
    atmosphere: { color: "#d4a06a", density: 0.3, hasCloud: true, cloudColor: "#d4c0a0", cloudCoverage: 0.15 },
    rotationSpeed: 0.003,
  },
  oceanic: {
    palette: { primary: "#2a7a6a", secondary: "#1a6a5a", liquid: "#0a4a7a", polar: "#b0d8e8" },
    surfaceDetail: "continents",
    liquidCoverage: 0.9,
    atmosphere: { color: "#44bbdd", density: 0.7, hasCloud: true, cloudColor: "#e8f4ff", cloudCoverage: 0.55 },
    rotationSpeed: 0.004,
  },
  volcanic: {
    palette: { primary: "#2a1a1a", secondary: "#1a0a0a", liquid: "#ff4500", polar: "#1a1a1a" },
    surfaceDetail: "volcanic",
    liquidCoverage: 0.3,
    atmosphere: { color: "#ff6633", density: 0.5, hasCloud: true, cloudColor: "#4a3030", cloudCoverage: 0.6 },
    rotationSpeed: 0.002,
  },
  ice: {
    palette: { primary: "#a8c8e0", secondary: "#88a8c8", liquid: "#2a4a6a", polar: "#e8f0f8" },
    surfaceDetail: "craters",
    liquidCoverage: 0.15,
    atmosphere: { color: "#88ccff", density: 0.35, hasCloud: true, cloudColor: "#d8e8f8", cloudCoverage: 0.3 },
    rotationSpeed: 0.002,
  },
  gas_giant: {
    palette: { primary: "#c8a050", secondary: "#a07830", liquid: "#e8c880", polar: "#d8c8a0" },
    surfaceDetail: "gas_bands",
    liquidCoverage: 0,
    atmosphere: { color: "#e8c880", density: 0.9, hasCloud: false, cloudColor: "#ffffff", cloudCoverage: 0 },
    rotationSpeed: 0.006,
  },
  toxic: {
    palette: { primary: "#8a8a40", secondary: "#6a7a30", liquid: "#5a6a20", polar: "#a0a060" },
    surfaceDetail: "continents",
    liquidCoverage: 0.4,
    atmosphere: { color: "#aacc44", density: 0.85, hasCloud: true, cloudColor: "#b0b868", cloudCoverage: 0.7 },
    rotationSpeed: 0.001,
  },
  barren: {
    palette: { primary: "#808080", secondary: "#606060", liquid: "#505050", polar: "#a0a0a0" },
    surfaceDetail: "craters",
    liquidCoverage: 0,
    atmosphere: { color: "#888888", density: 0.05, hasCloud: false, cloudColor: "#ffffff", cloudCoverage: 0 },
    rotationSpeed: 0.002,
  },
  crystalline: {
    palette: { primary: "#8a4a8a", secondary: "#6a3a7a", liquid: "#3a2a5a", polar: "#c088c0" },
    surfaceDetail: "crystalline",
    liquidCoverage: 0.2,
    atmosphere: { color: "#cc66ff", density: 0.4, hasCloud: false, cloudColor: "#ffffff", cloudCoverage: 0 },
    rotationSpeed: 0.002,
  },
};

function resolveConfig(visuals: PlanetVisuals): FullPlanetConfig {
  const preset = PRESETS[visuals.planetClass];
  return {
    radius: visuals.radius,
    palette: { ...preset.palette, ...visuals.palette },
    surfaceDetail: visuals.surfaceDetail ?? preset.surfaceDetail,
    liquidCoverage: visuals.liquidCoverage ?? preset.liquidCoverage,
    atmosphere: { ...preset.atmosphere, ...visuals.atmosphere },
    rotationSpeed: visuals.rotationSpeed ?? preset.rotationSpeed,
    rings: visuals.rings,
  };
}

// ── Texture generation ──

const textureCache = new Map<string, THREE.CanvasTexture>();
const cloudCache = new Map<string, THREE.CanvasTexture>();

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function lerpColor(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

function generatePlanetTexture(planetId: string, config: FullPlanetConfig): THREE.CanvasTexture {
  const key = planetId;
  if (textureCache.has(key)) return textureCache.get(key)!;

  const W = 512, H = 256;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const imgData = ctx.createImageData(W, H);
  const data = imgData.data;

  const seed = seedFromString(planetId);
  const rng = mulberry32(seed);
  const noise = createNoise2D(rng);
  const noise2 = createNoise2D(rng); // second noise for variation

  const liquidCol = hexToRgb(config.palette.liquid);
  const primaryCol = hexToRgb(config.palette.primary);
  const secondaryCol = hexToRgb(config.palette.secondary);
  const polarCol = hexToRgb(config.palette.polar);

  const isGasGiant = config.surfaceDetail === "gas_bands";
  const isVolcanic = config.surfaceDetail === "volcanic";
  const isCrystalline = config.surfaceDetail === "crystalline";

  for (let y = 0; y < H; y++) {
    // Latitude: 0 at equator, 1 at poles
    const lat = Math.abs((y / H) - 0.5) * 2;
    const polarBlend = Math.pow(Math.max(0, (lat - 0.7) / 0.3), 1.5);

    for (let x = 0; x < W; x++) {
      const idx = (y * W + x) * 4;

      // Spherical UV coordinates for seamless wrapping
      const u = x / W;
      const v = y / H;
      const theta = u * Math.PI * 2;
      const phi = v * Math.PI;
      // Map to 3D sphere surface — all three axes for distortion-free noise
      const nx = Math.cos(theta) * Math.sin(phi);
      const ny = Math.cos(phi);
      const nz = Math.sin(theta) * Math.sin(phi);

      let r: number, g: number, b: number;

      if (isGasGiant) {
        // Horizontal bands with turbulence
        const bandFreq = 8;
        const turbulence = triplanarFbm(noise, nx, ny, nz, 3, 4) * 0.15;
        const band = Math.sin((v + turbulence) * bandFreq * Math.PI);
        const t = band * 0.5 + 0.5;
        // Storm vortices
        const storm = triplanarFbm(noise2, nx, ny, nz, 5, 3);
        const stormIntensity = Math.max(0, storm - 0.3) * 2;
        const baseColor = lerpColor(primaryCol, secondaryCol, t);
        const brightened = lerpColor(baseColor, liquidCol, stormIntensity * 0.4);
        [r, g, b] = brightened;
      } else if (isVolcanic) {
        // Dark rock with lava cracks
        const terrain = triplanarFbm(noise, nx, ny, nz, 4, 5);
        const cracks = triplanarFbm(noise2, nx, ny, nz, 8, 3);
        const crackIntensity = Math.pow(Math.max(0, 1 - Math.abs(cracks) * 3), 4);
        const rockColor = lerpColor(primaryCol, secondaryCol, (terrain + 1) * 0.5);
        [r, g, b] = lerpColor(rockColor, liquidCol, crackIntensity * config.liquidCoverage * 3);
      } else if (isCrystalline) {
        // Faceted, angular noise patterns
        const crystal = triplanarFbm(noise, nx, ny, nz, 6, 3);
        const facets = Math.abs(crystal);
        const shimmer = triplanarFbm(noise2, nx, ny, nz, 12, 2);
        const t = Math.pow(facets, 0.6);
        const baseColor = lerpColor(primaryCol, secondaryCol, t);
        const highlight: [number, number, number] = [
          Math.min(255, baseColor[0] + 80),
          Math.min(255, baseColor[1] + 60),
          Math.min(255, baseColor[2] + 100),
        ];
        [r, g, b] = lerpColor(baseColor, highlight, Math.max(0, shimmer) * 0.6);
      } else {
        // Terrain with continents/ocean (terran, arid, oceanic, ice, toxic, barren)
        const height = triplanarFbm(noise, nx, ny, nz, 3, 5);
        const detail = triplanarFbm(noise2, nx, ny, nz, 6, 3) * 0.3;
        const elevation = height + detail;

        // Sea level based on liquid coverage — higher coverage = higher sea level
        const seaLevel = config.liquidCoverage * 0.8 - 0.3;

        if (elevation < seaLevel) {
          // Liquid (ocean/acid/ice melt)
          const depth = (seaLevel - elevation) / (seaLevel + 1);
          const deepLiquid: [number, number, number] = [
            liquidCol[0] * 0.6, liquidCol[1] * 0.6, liquidCol[2] * 0.6,
          ];
          [r, g, b] = lerpColor(liquidCol, deepLiquid, Math.min(1, depth * 2));
        } else {
          // Land
          const landT = Math.min(1, (elevation - seaLevel) * 2);
          [r, g, b] = lerpColor(primaryCol, secondaryCol, landT);

          // Craters for barren/ice
          if (config.surfaceDetail === "craters") {
            const craterNoise = triplanarFbm(noise2, nx, ny, nz, 10, 2);
            const crater = Math.max(0, 0.5 - Math.abs(craterNoise)) * 2;
            const shadow = 1 - crater * 0.4;
            r *= shadow; g *= shadow; b *= shadow;
          }
        }
      }

      // Polar blend
      if (polarBlend > 0) {
        [r, g, b] = lerpColor([r, g, b], polarCol, polarBlend);
      }

      data[idx] = Math.max(0, Math.min(255, r));
      data[idx + 1] = Math.max(0, Math.min(255, g));
      data[idx + 2] = Math.max(0, Math.min(255, b));
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  textureCache.set(key, tex);
  return tex;
}

function generateCloudTexture(planetId: string, config: FullPlanetConfig): THREE.CanvasTexture {
  const key = planetId + "_clouds";
  if (cloudCache.has(key)) return cloudCache.get(key)!;

  const W = 512, H = 256;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const imgData = ctx.createImageData(W, H);
  const data = imgData.data;

  const seed = seedFromString(planetId + "_cloud");
  const rng = mulberry32(seed);
  const noise = createNoise2D(rng);

  const cloudCol = hexToRgb(config.atmosphere.cloudColor);
  const coverage = config.atmosphere.cloudCoverage;

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const idx = (y * W + x) * 4;
      const u = x / W;
      const v = y / H;
      const theta = u * Math.PI * 2;
      const phi = v * Math.PI;
      const nx = Math.cos(theta) * Math.sin(phi);
      const ny = Math.cos(phi);
      const nz = Math.sin(theta) * Math.sin(phi);

      const n = triplanarFbm(noise, nx, ny, nz, 4, 4);
      // Threshold based on coverage — higher coverage = lower threshold
      const threshold = 1 - coverage * 1.8;
      const alpha = Math.max(0, (n - threshold) / (1 - threshold));
      const clamped = Math.min(1, alpha * 1.5);

      // Reduce clouds at poles slightly
      const lat = Math.abs(ny);
      const polarFade = lat > 0.85 ? 1 - (lat - 0.85) / 0.15 : 1;

      data[idx] = cloudCol[0];
      data[idx + 1] = cloudCol[1];
      data[idx + 2] = cloudCol[2];
      data[idx + 3] = Math.max(0, Math.min(255, clamped * polarFade * 180));
    }
  }

  ctx.putImageData(imgData, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  cloudCache.set(key, tex);
  return tex;
}

// ── Atmosphere fresnel shader ──

const ATMOS_VERTEX = `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mvPos.xyz);
    gl_Position = projectionMatrix * mvPos;
  }
`;

const ATMOS_FRAGMENT = `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  uniform vec3 color;
  uniform float density;
  void main() {
    float facing = abs(dot(vNormal, vViewDir));
    float glow = pow(1.0 - facing, 6.0);
    float alpha = glow * density;
    gl_FragColor = vec4(color, alpha);
  }
`;


// ── Shared geometry pool (avoid recreating per planet) ──

const geoCache = new Map<string, THREE.BufferGeometry>();

function getOrCreateGeo(key: string, factory: () => THREE.BufferGeometry): THREE.BufferGeometry {
  if (!geoCache.has(key)) geoCache.set(key, factory());
  return geoCache.get(key)!;
}

// ── Ring generation ──

function createRingMesh(radius: number, rings: NonNullable<PlanetVisuals["rings"]>): THREE.Mesh {
  const inner = radius * rings.innerRadius;
  const outer = radius * rings.outerRadius;
  const geo = new THREE.RingGeometry(inner, outer, 64, 1);
  // Generate a gradient texture for the ring
  const canvas = document.createElement("canvas");
  canvas.width = 256; canvas.height = 1;
  const ctx = canvas.getContext("2d")!;
  const ringCol = hexToRgb(rings.color);
  for (let x = 0; x < 256; x++) {
    const t = x / 255;
    // Denser near inner edge, gaps in middle/outer
    const density = (1 - t * 0.6) * (0.7 + Math.sin(t * 15) * 0.3);
    const alpha = Math.max(0, Math.min(1, density * rings.opacity));
    ctx.fillStyle = `rgba(${ringCol[0]},${ringCol[1]},${ringCol[2]},${alpha})`;
    ctx.fillRect(x, 0, 1, 1);
  }
  const tex = new THREE.CanvasTexture(canvas);

  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  // Rotate ring plane
  mesh.rotation.x = -Math.PI / 2 + THREE.MathUtils.degToRad(rings.tilt);
  return mesh;
}

// ── Main factory ──

export function createPlanetMesh(planetId: string, visuals: PlanetVisuals): THREE.Group {
  const config = resolveConfig(visuals);
  const group = new THREE.Group();
  const r = config.radius;

  // Body
  const bodyGeo = getOrCreateGeo(`sphere_${r}_32`, () => new THREE.SphereGeometry(r, 32, 32));
  const tex = generatePlanetTexture(planetId, config);
  const isVolcanic = config.surfaceDetail === "volcanic";
  const isCrystalline = config.surfaceDetail === "crystalline";

  const body = new THREE.Mesh(
    bodyGeo,
    new THREE.MeshStandardMaterial({
      map: tex,
      roughness: isVolcanic ? 0.95 : isCrystalline ? 0.3 : 0.85,
      metalness: isCrystalline ? 0.4 : 0.05,
      emissive: isVolcanic ? new THREE.Color(0x1a0500) : new THREE.Color(0x000000),
      emissiveIntensity: isVolcanic ? 0.8 : 0,
    })
  );
  body.name = "planet_body";
  group.userData.bodyMesh = body;
  group.add(body);

  // Emissive map for volcanic worlds — lava glow
  if (isVolcanic) {
    body.material.emissiveMap = tex;
  }

  // Cloud layer
  if (config.atmosphere.hasCloud && config.atmosphere.cloudCoverage > 0) {
    const cloudGeo = getOrCreateGeo(`sphere_${r * 1.02}_28`, () =>
      new THREE.SphereGeometry(r * 1.02, 28, 28)
    );
    const cloudTex = generateCloudTexture(planetId, config);
    const clouds = new THREE.Mesh(
      cloudGeo,
      new THREE.MeshStandardMaterial({
        map: cloudTex,
        transparent: true,
        opacity: 0.85,
        roughness: 1,
        metalness: 0,
        depthWrite: false,
      })
    );
    clouds.name = "clouds";
    group.add(clouds);
  }

  // Atmosphere — fresnel shader (skip for airless bodies)
  if (config.atmosphere.density > 0.05) {
    const atmosColor = new THREE.Color(config.atmosphere.color);

    const atmosGeo = getOrCreateGeo(`sphere_${r * 1.02}_32`, () =>
      new THREE.SphereGeometry(r * 1.02, 32, 32)
    );
    const atmos = new THREE.Mesh(
      atmosGeo,
      new THREE.ShaderMaterial({
        uniforms: {
          color: { value: atmosColor },
          density: { value: config.atmosphere.density * 0.25 },
        },
        vertexShader: ATMOS_VERTEX,
        fragmentShader: ATMOS_FRAGMENT,
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
      })
    );
    atmos.name = "atmosphere";
    group.add(atmos);
  }

  // Rings
  if (config.rings) {
    const ringMesh = createRingMesh(r, config.rings);
    ringMesh.name = "rings";
    group.add(ringMesh);
  }

  // Store config for animation
  group.userData.planetConfig = config;
  group.userData.planetRadius = r;

  return group;
}

// ── Default visuals for planets with no explicit config ──

export function defaultVisuals(planetClass: PlanetClass = "terran", radius = 40): PlanetVisuals {
  return { radius, planetClass };
}
